import React, { useRef, useState } from 'react';
import { Alert, Button, Modal, Space, Typography, message } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { fetchSqlDataWithQuery } from '../../services/api';
import SelectDCCFileModal from '../modals/SelectDCCFileModal';
import { useAuth } from './security/AuthContext';
import { dccDownloadUrl } from '../../services/dccApi';

const { Text } = Typography;

export type ExcelUploadSource = 'local' | 'dcc';

export type ExcelUploadResult = {
  file: File;
  source: ExcelUploadSource;
  rows: any[];
  sheetName?: string;
};

export type ExcelTemplateField = {
  field: string;
  header: string;
};

export interface ExcelUploadButtonProps {
  tableName: string;
  buttonText?: string;
  modalTitle?: string;
  disabled?: boolean;
  templateColumns?: string[];
  templateFields?: ExcelTemplateField[];
  templateSheetName?: string;
  templateFilename?: string;
  onLoaded: (result: ExcelUploadResult) => void;
}

const readFileToRows = async (file: File): Promise<{ rows: any[]; sheetName?: string }> => {
  const ext = (file.name.split('.').pop() || '').toLowerCase();

  if (ext === 'csv') {
    const text = await file.text();
    const wb = XLSX.read(text, { type: 'string' });
    const name = wb.SheetNames?.[0];
    const ws = name ? wb.Sheets[name] : undefined;
    const rows = ws ? (XLSX.utils.sheet_to_json(ws, { defval: '' }) as any[]) : [];
    return { rows, sheetName: name };
  }

  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const name = wb.SheetNames?.[0];
  const ws = name ? wb.Sheets[name] : undefined;
  const rows = ws ? (XLSX.utils.sheet_to_json(ws, { defval: '' }) as any[]) : [];
  return { rows, sheetName: name };
};

const ExcelUploadButton: React.FC<ExcelUploadButtonProps> = ({
  tableName,
  buttonText = 'Upload Excel/CSV',
  modalTitle = 'Upload Excel/CSV',
  disabled,
  templateColumns,
  templateFields,
  templateSheetName,
  templateFilename,
  onLoaded,
}) => {
  const { token } = useAuth();

  const normalizeTemplateFields = React.useCallback(() => {
    return (templateFields || [])
      .map((x) => ({
        field: String((x as any)?.field || '').trim(),
        header: String((x as any)?.header || '').trim(),
      }))
      .filter((x) => x.field && x.header);
  }, [templateFields]);

  const mapRowsFromHeadersToFields = React.useCallback(
    (rows: any[]) => {
      const pairs = normalizeTemplateFields();
      if (!pairs.length) return rows;
      return (rows || []).map((r: any) => {
        const next: any = {};
        pairs.forEach((p) => {
          next[p.field] = r?.[p.header];
        });
        return next;
      });
    },
    [normalizeTemplateFields]
  );

  const [open, setOpen] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [isDccPickerOpen, setIsDccPickerOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    const safe = String(tableName || '').trim();
    if (!safe) return;
    if (!/^[a-zA-Z0-9_]+$/.test(safe)) {
      message.error('Invalid table name');
      return;
    }

    setIsDownloadingTemplate(true);
    try {
      const providedPairs = normalizeTemplateFields();
      if (providedPairs.length) {
        const headers = providedPairs.map((p) => p.header);
        const emptyRow: any = {};
        headers.forEach((h: string) => {
          emptyRow[h] = '';
        });

        const worksheet = XLSX.utils.json_to_sheet([emptyRow]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, templateSheetName || 'Template');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, templateFilename || `${safe}_import_template.xlsx`);
        return;
      }

      const providedHeaders = (templateColumns || [])
        .map((c) => String(c || '').trim())
        .filter(Boolean);

      if (providedHeaders.length) {
        const emptyRow: any = {};
        providedHeaders.forEach((h: string) => {
          emptyRow[h] = '';
        });

        const worksheet = XLSX.utils.json_to_sheet([emptyRow]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, templateSheetName || 'Template');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, templateFilename || `${safe}_import_template.xlsx`);
        return;
      }

      const query = `
        SELECT
          column_name,
          data_type,
          is_nullable,
          is_generated,
          column_default,
          ordinal_position
        FROM information_schema.columns
        WHERE table_schema = 'public'
          AND table_name = '${safe}'
        ORDER BY ordinal_position;
      `;

      const resp: any = await fetchSqlDataWithQuery(query);
      const rows = Array.isArray(resp) ? resp : (resp?.data ?? []);
      const excluded = new Set(['id', 'created_at', 'updated_at', 'created_by', 'updated_by']);
      const cols = (rows || [])
        .map((r: any) => ({
          column_name: String(r?.column_name || ''),
          column_default: r?.column_default,
          is_generated: String(r?.is_generated || 'NEVER'),
        }))
        .filter((r: any) => r.column_name)
        .filter((r: any) => !excluded.has(r.column_name))
        // Exclude generated columns (e.g., generated always as (...) stored)
        .filter((r: any) => String(r?.is_generated || 'NEVER') === 'NEVER')
        .filter((r: any) => !String(r?.column_default || '').toLowerCase().includes('nextval('));

      if (!cols.length) {
        message.warning('No columns found for template (after exclusions)');
        return;
      }

      const headers = cols.map((c: any) => c.column_name);
      const emptyRow: any = {};
      headers.forEach((h: string) => {
        emptyRow[h] = '';
      });

      const worksheet = XLSX.utils.json_to_sheet([emptyRow]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, templateSheetName || 'Template');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, templateFilename || `${safe}_import_template.xlsx`);
    } catch (e: any) {
      message.error(e?.message || 'Failed to download template');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleLocalFileSelected = async (file: File) => {
    setErrorText(null);
    setIsLoadingFile(true);
    try {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!ext || !['csv', 'xls', 'xlsx'].includes(ext)) {
        message.error('Only CSV or Excel files are allowed');
        return;
      }

      const parsed = await readFileToRows(file);
      const mappedRows = mapRowsFromHeadersToFields(parsed.rows);
      onLoaded({ file, source: 'local', rows: mappedRows, sheetName: parsed.sheetName });
      setOpen(false);
    } catch (e: any) {
      const msg = e?.message || 'Failed to read file';
      setErrorText(String(msg));
      message.error(String(msg));
    } finally {
      setIsLoadingFile(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDccPick = async (args: { fullPath: string; name: string }) => {
    setIsDccPickerOpen(false);
    setErrorText(null);

    const ext = (args.name.split('.').pop() || '').toLowerCase();
    if (!ext || !['csv', 'xls', 'xlsx'].includes(ext)) {
      message.error('Only CSV or Excel files are allowed');
      return;
    }

    if (!token) {
      message.error('Not authenticated');
      return;
    }

    setIsLoadingFile(true);
    try {
      const url = dccDownloadUrl(args.fullPath, token, true);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`DCC download failed: ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], args.name, { type: blob.type || 'application/octet-stream' });

      const parsed = await readFileToRows(file);
      const mappedRows = mapRowsFromHeadersToFields(parsed.rows);
      onLoaded({ file, source: 'dcc', rows: mappedRows, sheetName: parsed.sheetName });
      setOpen(false);
    } catch (e: any) {
      const msg = e?.message || 'Failed to download/read DCC file';
      setErrorText(String(msg));
      message.error(String(msg));
    } finally {
      setIsLoadingFile(false);
    }
  };

  return (
    <>
      <Button
        icon={<UploadOutlined />}
        onClick={() => {
          setErrorText(null);
          setOpen(true);
        }}
        disabled={disabled}
      >
        {buttonText}
      </Button>

      <Modal
        open={open}
        title={modalTitle}
        onCancel={() => setOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Text type="secondary">Target table: {String(tableName || '')}</Text>

          {errorText ? (
            <Alert type="error" showIcon message={errorText} />
          ) : null}

          <Space wrap>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              loading={isDownloadingTemplate}
              disabled={isLoadingFile || isDownloadingTemplate}
            >
              Download Template
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={() => inputRef.current?.click()}
              loading={isLoadingFile}
              disabled={isLoadingFile}
            >
              Select Local File
            </Button>
            <Button
              onClick={() => setIsDccPickerOpen(true)}
              loading={isLoadingFile}
              disabled={isLoadingFile}
            >
              Select from DCC
            </Button>
          </Space>

          <input
            type="file"
            accept=".csv,.xls,.xlsx"
            ref={inputRef}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleLocalFileSelected(f);
            }}
          />
        </Space>
      </Modal>

      <SelectDCCFileModal
        open={isDccPickerOpen}
        mode="single"
        onCancel={() => setIsDccPickerOpen(false)}
        onSelect={(args) => {
          handleDccPick(args);
        }}
      />
    </>
  );
};

export default ExcelUploadButton;
